import { useMemo } from "react";

export interface VerdictBucket {
  hour: number;
  allow: number;
  ask: number;
  deny: number;
}

interface Props {
  buckets: VerdictBucket[];
  height?: number;
}

// Round a raw axis max up to a "nice" round value (5/10/25/50/100 steps)
// so the gridlines land on legible numbers instead of arbitrary maxima.
function niceMax(v: number): number {
  if (v <= 1) return 1;
  if (v <= 5) return 5;
  if (v <= 10) return 10;
  if (v <= 25) return Math.ceil(v / 5) * 5;
  if (v <= 50) return Math.ceil(v / 10) * 10;
  if (v <= 100) return Math.ceil(v / 25) * 25;
  return Math.ceil(v / 50) * 50;
}

// Reactive 24h verdict-volume chart: a professional stacked bar chart
// (allow / ask / deny, bottom→top) with a y-axis, gridlines, and the single
// biggest deny/ask spike flagged as an anomaly — mirrors the steerly.ai
// Security Room "activity volume + anomaly" mockup using the data we have.
export function VerdictVolumeChart({ buckets, height = 190 }: Props) {
  const W = 720;
  const H = height;
  const gutterL = 30;
  const gutterB = 22;
  const padTop = 14;
  const padR = 8;

  const { bars, gridLines, totals, anomaly, max } = useMemo(() => {
    const n = buckets.length || 24;
    const totalsByHour = buckets.map((b) => b.allow + b.ask + b.deny);
    const maxV = Math.max(1, ...totalsByHour);
    const niceAxisMax = niceMax(maxV);

    const plotL = gutterL;
    const plotR = W - padR;
    const plotW = plotR - plotL;
    const plotTop = padTop;
    const plotBottom = H - gutterB;
    const plotH = plotBottomGuard(plotBottom, plotTop);
    // Map a stacked value (0..niceAxisMax) to a pixel height within the plot.
    const hOf = (v: number) => (v / niceAxisMax) * plotH;
    // Pixel y for a value measured from the baseline up.
    const yOf = (v: number) => plotBottom - hOf(v);

    const slot = plotW / n;
    const gap = Math.min(6, slot * 0.28);
    const barW = Math.max(1, slot - gap);
    const MIN_SEG = 2; // tiny nonzero counts still get a visible sliver

    // Build per-bar stacked segments bottom→top: allow, ask, deny.
    const order: Array<{ key: "allow" | "ask" | "deny"; color: string }> = [
      { key: "allow", color: "var(--green, #4ec27a)" },
      { key: "ask", color: "var(--amber, #e0a64a)" },
      { key: "deny", color: "var(--red, #f06b62)" },
    ];

    const barsOut = buckets.map((b, i) => {
      const bx = plotL + slot * i + gap / 2;
      let cursor = plotBottom; // baseline; stack upward
      const segs: Array<{ y: number; h: number; color: string }> = [];
      let topMost = -1;
      order.forEach(({ key, color }) => {
        const count = b[key];
        if (count <= 0) return;
        const rawH = hOf(count);
        const segH = Math.max(MIN_SEG, rawH);
        cursor -= segH;
        topMost = segs.length;
        segs.push({ y: cursor, h: segH, color });
      });
      return {
        x: bx,
        w: barW,
        segs,
        topIndex: topMost,
        top: cursor, // y of the topmost pixel of the whole bar
        allow: b.allow,
        ask: b.ask,
        deny: b.deny,
        index: i,
      };
    });

    // Anomaly = the hour with the largest deny count (>0), else largest ask.
    let anomalyIdx = -1;
    let anomalyKind: "deny" | "ask" | null = null;
    let best = 0;
    buckets.forEach((b, i) => {
      if (b.deny > best) {
        best = b.deny;
        anomalyIdx = i;
        anomalyKind = "deny";
      }
    });
    if (anomalyIdx === -1) {
      best = 0;
      buckets.forEach((b, i) => {
        if (b.ask > best) {
          best = b.ask;
          anomalyIdx = i;
          anomalyKind = "ask";
        }
      });
    }
    const anomalyOut =
      anomalyIdx >= 0 && best > 0
        ? {
            barX: barsOut[anomalyIdx].x,
            barW: barsOut[anomalyIdx].w,
            barTop: barsOut[anomalyIdx].top,
            barBottom: plotBottom,
            kind: anomalyKind!,
            count: best,
          }
        : null;

    // 3 gridlines: 0, mid, max — deduped by LABEL. At niceAxisMax === 1 the
    // midpoint is 0.5, which rounds to 1 and printed the axis as "1, 1, 0":
    // two identical labels at different heights, which reads as a rendering
    // bug on an otherwise empty chart.
    const seenLabels = new Set<number>();
    // Round the VALUE FIRST, then position the line at that rounded value.
    // Rounding only the label drew "2" at the height of 1.5, so the axis
    // disagreed with the bars it was meant to measure.
    const gridLinesOut = [niceAxisMax, niceAxisMax / 2, 0]
      .map((v) => Math.round(v))
      .map((value) => ({ value, y: yOf(value) }))
      .filter((g) => {
        if (seenLabels.has(g.value)) return false;
        seenLabels.add(g.value);
        return true;
      });

    return {
      bars: barsOut,
      gridLines: gridLinesOut,
      totals: {
        allow: buckets.reduce((s, b) => s + b.allow, 0),
        ask: buckets.reduce((s, b) => s + b.ask, 0),
        deny: buckets.reduce((s, b) => s + b.deny, 0),
      },
      anomaly: anomalyOut,
      max: maxV,
    };
  }, [buckets, H]);

  const hasData = totals.allow + totals.ask + totals.deny > 0;

  return (
    <div className="sr-chart">
      <div className="sr-chart__head">
        <div className="sr-chart__title">Policy verdicts · 24h</div>
        <div className="sr-chart__legend">
          <span className="sr-legend allow">
            <i /> Allowed {totals.allow}
          </span>
          <span className="sr-legend ask">
            <i /> Asked {totals.ask}
          </span>
          <span className="sr-legend deny">
            <i /> Denied {totals.deny}
          </span>
        </div>
      </div>

      <svg
        className="sr-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Activity volume over 24 hours: ${totals.allow} allowed, ${totals.ask} asked, ${totals.deny} denied`}
      >
        {/* gridlines + y-axis labels */}
        {gridLines.map((g, i) => (
          <g key={`grid-${i}`}>
            <line
              x1={gutterL}
              x2={W - padR}
              y1={g.y}
              y2={g.y}
              stroke="var(--line, #26272c)"
              strokeWidth="1"
              strokeOpacity={i === 0 ? 0.9 : 0.45}
            />
            <text
              x={gutterL - 6}
              y={g.y + 3}
              textAnchor="end"
              fill="var(--muted, #8a8e96)"
              fontSize="9"
              fontFamily="var(--font-mono, ui-monospace, monospace)"
            >
              {g.value}
            </text>
          </g>
        ))}

        {hasData ? (
          <>
            {bars.map((bar) => (
              <g key={`bar-${bar.index}`}>
                <title>
                  {bar.allow} allowed · {bar.ask} asked · {bar.deny} denied
                </title>
                {bar.segs.map((seg, si) => {
                  const isTop = si === bar.topIndex;
                  return (
                    <rect
                      key={`seg-${bar.index}-${si}`}
                      x={bar.x}
                      y={seg.y}
                      width={bar.w}
                      height={seg.h}
                      rx={isTop ? 2 : 0}
                      ry={isTop ? 2 : 0}
                      fill={seg.color}
                    />
                  );
                })}
              </g>
            ))}

            {anomaly ? (
              <g>
                {/* highlight outline on the anomaly bar */}
                <rect
                  x={anomaly.barX - 0.5}
                  y={anomaly.barTop - 0.5}
                  width={anomaly.barW + 1}
                  height={anomaly.barBottom - anomaly.barTop + 1}
                  fill="none"
                  rx="2"
                  ry="2"
                  stroke={anomaly.kind === "deny" ? "var(--red, #f06b62)" : "var(--amber, #e0a64a)"}
                  strokeWidth="1"
                  strokeOpacity="0.9"
                />
                {/* downward triangle marker centered just above the bar */}
                {(() => {
                  const cx = anomaly.barX + anomaly.barW / 2;
                  const ty = anomaly.barTop - 7;
                  const half = 4;
                  const tip = anomaly.barTop - 1;
                  return (
                    <polygon
                      points={`${cx - half},${ty} ${cx + half},${ty} ${cx},${tip}`}
                      fill={
                        anomaly.kind === "deny" ? "var(--red, #f06b62)" : "var(--amber, #e0a64a)"
                      }
                    />
                  );
                })()}
              </g>
            ) : null}
          </>
        ) : (
          <text
            x={W / 2}
            y={H / 2}
            textAnchor="middle"
            fill="var(--muted, #8a8e96)"
            fontSize="13"
            fontFamily="var(--font-sans)"
          >
            No agent activity in the last 24h
          </text>
        )}

        {/* hour ticks */}
        <text x={gutterL} y={H - 6} fill="var(--muted, #8a8e96)" fontSize="10">
          24h ago
        </text>
        <text x={W - padR} y={H - 6} textAnchor="end" fill="var(--muted, #8a8e96)" fontSize="10">
          now
        </text>
      </svg>

      {anomaly ? (
        <div className={`sr-chart__anomaly ${anomaly.kind}`}>
          <span className="sr-chart__anomaly-dot" />
          Spike: {anomaly.count} {anomaly.kind === "deny" ? "denied" : "held"} in one hour ·{" "}
          {Math.round((anomaly.count / max) * 100)}% of the busiest hour’s volume
        </div>
      ) : null}
    </div>
  );
}

// Clamp the plot height to a sane minimum so a tiny `height` prop can't
// produce negative/zero bar heights.
function plotBottomGuard(plotBottom: number, plotTop: number): number {
  return Math.max(1, plotBottom - plotTop);
}
